import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Stack, Chip,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, Alert, Tooltip, CircularProgress, Popover,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Shield as ShieldIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  AutoAwesome as AutoAwesomeIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { dailyFocusBlocksApi, calendarApi } from '../api/services';
import type { DailyFocusBlock, CalendarEvent } from '../types/models';
import { focusTimeCopy } from '../copy/focusTime';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const HOURS = Array.from({ length: 13 }, (_, i) => i + 7); // 7am–7pm

// Focus block colours by source
const SOURCE_COLOURS: Record<string, string> = {
  AutoAllocated: 'rgba(108, 155, 255, 0.35)',
  Manual: 'rgba(129, 199, 132, 0.35)',
  DetectedFromCalendar: 'rgba(77, 208, 225, 0.35)',
};
const MEETING_COLOUR = 'rgba(255, 82, 82, 0.25)';
const CONFLICT_COLOUR = 'rgba(255, 82, 82, 0.5)';

function timeStrToHour(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h + m / 60;
}

function formatHour(hour: number): string {
  const h = Math.floor(hour);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const display = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${display} ${ampm}`;
}

function getMonday(d: Date): Date {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function formatDateOnly(d: Date): string {
  return d.toISOString().split('T')[0];
}

function formatShortDate(d: Date): string {
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function addDays(d: Date, n: number): Date {
  const result = new Date(d);
  result.setDate(result.getDate() + n);
  return result;
}

function sourceLabel(source: string): string {
  switch (source) {
    case 'AutoAllocated': return 'Auto';
    case 'Manual': return 'Manual';
    case 'DetectedFromCalendar': return 'Detected';
    default: return source;
  }
}

export default function FocusTimePage() {
  const [weekStart, setWeekStart] = useState<Date>(() => getMonday(new Date()));
  const [focusBlocks, setFocusBlocks] = useState<DailyFocusBlock[]>([]);
  const [gridEvents, setGridEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);

  // Add manual block dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addDate, setAddDate] = useState('');
  const [addStart, setAddStart] = useState('09:00');
  const [addEnd, setAddEnd] = useState('11:00');
  const [addLabel, setAddLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // Conflict resolution popover. Anchored on the conflict cell so the
  // Keep/Remove actions are attached directly to the moment the user clicks
  // the conflict rather than hidden in a menu bar.
  const [conflictAnchor, setConflictAnchor] = useState<HTMLElement | null>(null);
  const [conflictBlock, setConflictBlock] = useState<DailyFocusBlock | null>(null);
  const [conflictMeetingTitle, setConflictMeetingTitle] = useState<string>('');
  // Blocks the user has explicitly chosen to keep despite a conflict. Held in
  // component state only — the conflict returns if the page reloads, which is
  // the right behaviour: ack is for the current glance, not a persisted flag.
  const [acknowledgedConflicts, setAcknowledgedConflicts] = useState<Set<string>>(() => new Set());

  // The 5 weekday dates for the current week
  const weekDates = DAYS.map((_, i) => addDays(weekStart, i));
  const weekEnd = addDays(weekStart, 6);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [blocks, events] = await Promise.all([
        dailyFocusBlocksApi.getByRange(formatDateOnly(weekStart), formatDateOnly(weekEnd)),
        calendarApi.getByRange(formatDateOnly(weekStart), formatDateOnly(weekEnd)),
      ]);
      setFocusBlocks(blocks);
      setGridEvents(events.filter(e => !e.isAllDay));
    } catch (err) {
      console.error('Failed to load focus data', err);
    } finally {
      setLoading(false);
    }
  }, [weekStart]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this focus block? Its calendar event will also be removed.')) return;
    try {
      await dailyFocusBlocksApi.delete(id);
      setFocusBlocks(prev => prev.filter(b => b.id !== id));
    } catch (err) {
      console.error('Failed to delete focus block', err);
    }
  };

  const openConflictPopover = (anchor: HTMLElement, block: DailyFocusBlock, meetingTitle: string) => {
    setConflictAnchor(anchor);
    setConflictBlock(block);
    setConflictMeetingTitle(meetingTitle);
  };
  const closeConflictPopover = () => {
    setConflictAnchor(null);
    setConflictBlock(null);
    setConflictMeetingTitle('');
  };
  const handleKeepFocus = () => {
    if (!conflictBlock) return;
    setAcknowledgedConflicts((prev) => {
      const next = new Set(prev);
      next.add(conflictBlock.id);
      return next;
    });
    closeConflictPopover();
  };
  const handleRemoveFocus = async () => {
    if (!conflictBlock) return;
    const id = conflictBlock.id;
    closeConflictPopover();
    try {
      await dailyFocusBlocksApi.delete(id);
      setFocusBlocks((prev) => prev.filter((b) => b.id !== id));
    } catch (err) {
      console.error('Failed to remove focus block', err);
    }
  };

  const openAddDialog = (date?: string) => {
    setAddDate(date ?? formatDateOnly(weekDates[0]));
    setAddStart('09:00');
    setAddEnd('11:00');
    setAddLabel('');
    setAddError(null);
    setAddDialogOpen(true);
  };

  const handleSaveBlock = async () => {
    if (!addDate || !addStart || !addEnd) return;
    if (addEnd <= addStart) { setAddError('End time must be after start time.'); return; }
    setSaving(true);
    setAddError(null);
    try {
      const block = await dailyFocusBlocksApi.create({
        date: addDate,
        startTime: addStart,
        endTime: addEnd,
        label: addLabel || 'Focus Time',
      });
      setFocusBlocks(prev => [...prev, block]);
      setAddDialogOpen(false);
    } catch {
      setAddError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Build lookups for the grid
  // blocksByDateStr: date string → blocks for that day
  const blocksByDateStr: Record<string, DailyFocusBlock[]> = {};
  weekDates.forEach(d => { blocksByDateStr[formatDateOnly(d)] = []; });
  focusBlocks.forEach(b => {
    if (blocksByDateStr[b.date]) blocksByDateStr[b.date].push(b);
  });

  // meetingsByDateStr: date string → meetings for that day
  const meetingsByDateStr: Record<string, { startHour: number; endHour: number; title: string }[]> = {};
  weekDates.forEach(d => { meetingsByDateStr[formatDateOnly(d)] = []; });
  gridEvents.forEach(evt => {
    const start = new Date(evt.startTime);
    const end = new Date(evt.endTime);
    const ds = start.toISOString().split('T')[0];
    if (meetingsByDateStr[ds]) {
      meetingsByDateStr[ds].push({
        startHour: start.getHours() + start.getMinutes() / 60,
        endHour: end.getHours() + end.getMinutes() / 60,
        title: evt.title,
      });
    }
  });

  // Summary stats
  const totalFocusMinutes = focusBlocks.reduce((sum, b) => {
    const s = timeStrToHour(b.startTime);
    const e = timeStrToHour(b.endTime);
    return sum + (e - s) * 60;
  }, 0);
  const totalFocusHours = Math.round(totalFocusMinutes / 60 * 10) / 10;
  const daysWithFocus = new Set(focusBlocks.map(b => b.date)).size;
  const autoBlocks = focusBlocks.filter(b => b.source === 'AutoAllocated').length;
  const manualBlocks = focusBlocks.filter(b => b.source === 'Manual').length;

  const isCurrentWeek = formatDateOnly(getMonday(new Date())) === formatDateOnly(weekStart);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h4">Focus Time</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Week of {formatShortDate(weekStart)} to {formatShortDate(weekEnd)}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton onClick={() => setWeekStart(d => addDays(d, -7))} size="small">
            <PrevIcon />
          </IconButton>
          <Button
            variant="text"
            size="small"
            disabled={isCurrentWeek}
            onClick={() => setWeekStart(getMonday(new Date()))}
          >
            This week
          </Button>
          <IconButton onClick={() => setWeekStart(d => addDays(d, 7))} size="small">
            <NextIcon />
          </IconButton>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openAddDialog()}>
            Add Block
          </Button>
        </Stack>
      </Box>

      {/* Summary cards */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Focus This Week</Typography>
            <Typography variant="h5">{totalFocusHours}h</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Blocks</Typography>
            <Typography variant="h5">{focusBlocks.length}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Days Covered</Typography>
            <Typography variant="h5">{daysWithFocus} / 5</Typography>
          </Box>
          <Box sx={{ borderLeft: 1, borderColor: 'divider', pl: 3 }}>
            <Typography variant="caption" color="text.secondary">Auto-allocated</Typography>
            <Typography variant="h5">{autoBlocks}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Manual</Typography>
            <Typography variant="h5">{manualBlocks}</Typography>
          </Box>
          {totalFocusHours > 0 && (
            <Box sx={{ borderLeft: 1, borderColor: 'divider', pl: 3 }}>
              <Typography variant="caption" color="text.secondary">% of 40h week</Typography>
              <Typography variant="h5">{Math.round((totalFocusHours / 40) * 100)}%</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* First-run explainer. Shown only when this week has no blocks at
          all and we are not still loading. Teaches what focus time is,
          why the grid is empty, and how to start — per the §6 empty-state
          rule. */}
      {!loading && focusBlocks.length === 0 && (
        <Card
          sx={{
            mb: 3,
            bgcolor: 'rgba(108, 155, 255, 0.04)',
            border: 1,
            borderColor: 'rgba(108, 155, 255, 0.25)',
          }}
        >
          <CardContent sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
            <ShieldIcon sx={{ color: 'primary.main', mt: 0.25 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                {focusTimeCopy.firstRunHeading}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {focusTimeCopy.firstRunBody}
              </Typography>
              <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => openAddDialog()}>
                {focusTimeCopy.firstRunAction}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Shield info */}
      <Alert
        severity="info"
        icon={<ShieldIcon />}
        sx={{ mb: 3, bgcolor: 'rgba(108, 155, 255, 0.06)', border: '1px solid rgba(108, 155, 255, 0.15)' }}
      >
        <Typography variant="body2">
          <strong>Focus blocks protect your thinking time.</strong> The scheduling engine places work items
          in non-focus slots first. Focus blocks are only used as overflow when all other time is full.
          Run <strong>Start My Day</strong> to auto-allocate fresh blocks each morning.
        </Typography>
      </Alert>

      <Grid container spacing={2.5}>
        {/* Weekly Grid */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Weekly Focus Grid
                </Typography>
                {loading && <CircularProgress size={16} />}
              </Box>

              {/* Legend */}
              <Stack direction="row" spacing={2} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: SOURCE_COLOURS.AutoAllocated }} />
                  <Typography variant="caption" color="text.secondary">Auto-allocated</Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: SOURCE_COLOURS.Manual }} />
                  <Typography variant="caption" color="text.secondary">Manual</Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: SOURCE_COLOURS.DetectedFromCalendar }} />
                  <Typography variant="caption" color="text.secondary">Detected</Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: MEETING_COLOUR }} />
                  <Typography variant="caption" color="text.secondary">Meeting</Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: CONFLICT_COLOUR, border: 1, borderColor: 'error.main' }} />
                  <Typography variant="caption" color="text.secondary">Conflict</Typography>
                </Stack>
              </Stack>

              <Box sx={{ overflowX: 'auto' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '60px repeat(5, 1fr)', gap: 0, minWidth: 500 }}>
                  {/* Day headers */}
                  <Box />
                  {DAYS.map((day, i) => (
                    <Box key={day} sx={{ textAlign: 'center', py: 1 }}>
                      <Typography variant="caption" fontWeight={600} color="text.secondary">
                        {day.substring(0, 3)}
                      </Typography>
                      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.65rem' }}>
                        {formatShortDate(weekDates[i])}
                      </Typography>
                    </Box>
                  ))}

                  {/* Hour rows */}
                  {HOURS.map(hour => (
                    <React.Fragment key={hour}>
                      <Box sx={{ display: 'flex', alignItems: 'center', pr: 1, justifyContent: 'flex-end' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          {formatHour(hour)}
                        </Typography>
                      </Box>
                      {weekDates.map((date, di) => {
                        const ds = formatDateOnly(date);
                        const dayBlocks = blocksByDateStr[ds] || [];
                        const dayMeetings = meetingsByDateStr[ds] || [];

                        const activeBlock = dayBlocks.find(b => {
                          const s = timeStrToHour(b.startTime);
                          const e = timeStrToHour(b.endTime);
                          return hour >= s && hour < e;
                        });
                        const activeMeeting = dayMeetings.find(m =>
                          hour >= m.startHour && hour < m.endHour
                        );

                        const hasFocus = !!activeBlock;
                        const hasMeeting = !!activeMeeting;
                        const rawConflict = hasFocus && hasMeeting;
                        // A conflict the user has already acknowledged ("Keep focus")
                        // still draws as a focus cell — just no warning treatment.
                        const isConflict = rawConflict && !acknowledgedConflicts.has(activeBlock!.id);

                        let cellBg = 'rgba(255,255,255,0.02)';
                        if (isConflict) cellBg = CONFLICT_COLOUR;
                        else if (hasFocus) cellBg = SOURCE_COLOURS[activeBlock!.source] ?? SOURCE_COLOURS.AutoAllocated;
                        else if (hasMeeting) cellBg = MEETING_COLOUR;

                        const isBlockStart = activeBlock && hour === Math.ceil(timeStrToHour(activeBlock.startTime));
                        const isMeetingStart = activeMeeting && hour === Math.ceil(activeMeeting.startHour);
                        let cellLabel = '';
                        if (isConflict && (isBlockStart || isMeetingStart)) {
                          cellLabel = `⚠ ${activeMeeting!.title}`;
                        } else if (isBlockStart) {
                          cellLabel = activeBlock!.label;
                        } else if (isMeetingStart && !hasFocus) {
                          cellLabel = activeMeeting!.title;
                        }

                        // Focus cells are clickable only on conflict (opens the
                        // Keep/Remove popover). Non-conflict focus cells draw a
                        // lock icon to signal "pinned"; they are not clickable
                        // themselves — use the block list for edit actions.
                        // Using event.currentTarget as the popover anchor keeps
                        // the handler allocation-free per render.
                        const clickable = isConflict && !!activeBlock;

                        const tooltipText = isConflict
                          ? `Conflict: "${activeMeeting!.title}" overlaps with "${activeBlock!.label}". Click to resolve.`
                          : hasFocus && isBlockStart
                            ? `Focus block protected. ${activeBlock!.label}`
                            : '';

                        return (
                          <Tooltip
                            key={`${di}-${hour}`}
                            title={tooltipText}
                            placement="top"
                            arrow
                            disableHoverListener={!tooltipText}
                          >
                            <Box
                              role={clickable ? 'button' : undefined}
                              tabIndex={clickable ? 0 : undefined}
                              onClick={
                                clickable
                                  ? (e) => openConflictPopover(e.currentTarget as HTMLElement, activeBlock!, activeMeeting!.title)
                                  : undefined
                              }
                              onKeyDown={
                                clickable
                                  ? (e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        openConflictPopover(
                                          e.currentTarget as HTMLElement,
                                          activeBlock!,
                                          activeMeeting!.title,
                                        );
                                      }
                                    }
                                  : undefined
                              }
                              sx={{
                                position: 'relative',
                                height: 32,
                                bgcolor: cellBg,
                                border: 1,
                                borderColor: isConflict ? 'error.main' : 'divider',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                transition: 'background-color 120ms',
                                cursor: clickable ? 'pointer' : 'default',
                              }}
                            >
                              {/* Lock glyph on the start cell of a protected
                                  focus block. Communicates immovable without
                                  adding text clutter. Suppressed on conflict
                                  where the warning icon takes priority. */}
                              {hasFocus && isBlockStart && !isConflict && (
                                <LockIcon
                                  sx={{
                                    position: 'absolute',
                                    left: 3,
                                    top: 3,
                                    fontSize: 12,
                                    color: 'text.secondary',
                                    opacity: 0.7,
                                  }}
                                />
                              )}
                              {cellLabel && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: '0.6rem',
                                    fontWeight: isConflict ? 700 : 500,
                                    color: isConflict ? 'error.main' : 'text.primary',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    px: 0.5,
                                    pl: hasFocus && isBlockStart && !isConflict ? 2 : 0.5,
                                  }}
                                >
                                  {cellLabel}
                                </Typography>
                              )}
                            </Box>
                          </Tooltip>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Blocks list */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                This Week's Blocks
              </Typography>

              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {!loading && focusBlocks.length === 0 && (
                <Box sx={{ py: 2, textAlign: 'center' }}>
                  <AutoAwesomeIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary" variant="body2">
                    No focus blocks this week
                  </Typography>
                  <Typography color="text.disabled" variant="caption">
                    Run Start My Day to auto-allocate, or add one manually
                  </Typography>
                </Box>
              )}

              <Stack spacing={1} sx={{ mt: 1 }}>
                {focusBlocks
                  .slice()
                  .sort((a, b) => a.date.localeCompare(b.date) || a.startTime.localeCompare(b.startTime))
                  .map(block => {
                    const blockDate = new Date(block.date + 'T00:00:00');
                    const dayName = blockDate.toLocaleDateString('en-GB', { weekday: 'short' });
                    const dateStr = formatShortDate(blockDate);
                    const startH = block.startTime.substring(0, 5);
                    const endH = block.endTime.substring(0, 5);
                    const durationMins = Math.round(
                      (timeStrToHour(block.endTime) - timeStrToHour(block.startTime)) * 60
                    );

                    return (
                      <Box
                        key={block.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 1,
                          borderRadius: 1,
                          bgcolor: 'rgba(255,255,255,0.03)',
                          border: 1,
                          borderColor: 'divider',
                          gap: 1,
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" noWrap fontWeight={500}>
                            {block.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {dayName} {dateStr} · {startH} to {endH} ({durationMins}m)
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Chip
                            label={sourceLabel(block.source)}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              bgcolor: SOURCE_COLOURS[block.source] ?? 'rgba(255,255,255,0.08)',
                              color: 'text.primary',
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(block.id)}
                            sx={{ p: 0.25 }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Box>
                    );
                  })}
              </Stack>

              {focusBlocks.length > 0 && (
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => openAddDialog()}
                  sx={{ mt: 2, width: '100%' }}
                  variant="outlined"
                >
                  Add manual block
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add manual block dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Focus Block</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Date"
              type="date"
              value={addDate}
              onChange={e => setAddDate(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Start"
                type="time"
                value={addStart}
                onChange={e => setAddStart(e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End"
                type="time"
                value={addEnd}
                onChange={e => setAddEnd(e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            <TextField
              label="Label (optional)"
              value={addLabel}
              onChange={e => setAddLabel(e.target.value)}
              fullWidth
              size="small"
              placeholder="Focus Time"
            />
            {addError && <Alert severity="error">{addError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveBlock} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Conflict resolution popover. Anchored on the clicked cell; closes on
          blur or explicit action. Two options only ("Keep focus" or
          "Remove focus block") per the §5.3 rule: no third option. */}
      <Popover
        open={!!conflictAnchor}
        anchorEl={conflictAnchor}
        onClose={closeConflictPopover}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{ paper: { sx: { p: 2, maxWidth: 320, borderRadius: 2 } } }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          {focusTimeCopy.conflictTitle}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {conflictMeetingTitle
            ? `"${conflictMeetingTitle}" overlaps this focus block.`
            : focusTimeCopy.conflictBody}
        </Typography>
        <Stack spacing={1}>
          <Button
            fullWidth
            variant="contained"
            size="small"
            startIcon={<ShieldIcon />}
            onClick={handleKeepFocus}
          >
            {focusTimeCopy.conflictKeep}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            size="small"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleRemoveFocus}
          >
            {focusTimeCopy.conflictRemove}
          </Button>
        </Stack>
      </Popover>
    </Box>
  );
}
